"use client"

import { useState } from "react"
import { Search, MapPin, TrendingUp, Building, BarChart3, Target, Info } from "lucide-react"
import { cn } from "@/utils/common"
import { Badge } from "@/components/ui/common/Badge"
import { Input } from "@/components/forms/inputs/text/Input"
import { Button } from "@/components/ui/common/Button"

interface RegionData {
  id: number
  name: string
  demandLevel: "Bardzo wysoki" | "Wysoki" | "Średni" | "Niski"
  jobCount: number
  averageSalary: string
  topProfessions: string[]
  growthRate: number
  companies: number
}

interface Profession {
  id: number
  name: string
  category: string
  demandByRegion: {
    region: string
    demand: number
    salary: string
    companies: number
  }[]
  totalDemand: number
  averageSalary: string
}

export default function MapaZawodow() {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null)
  const [selectedProfession, setSelectedProfession] = useState("Wszystkie")
  const [searchTerm, setSearchTerm] = useState("")

  const regions: RegionData[] = [
    {
      id: 1,
      name: "Mazowieckie",
      demandLevel: "Bardzo wysoki",
      jobCount: 45000,
      averageSalary: "8 500 zł",
      topProfessions: ["Programista", "Analityk danych", "Manager projektu"],
      growthRate: 15.2,
      companies: 12500,
    },
    {
      id: 2,
      name: "Małopolskie",
      demandLevel: "Wysoki",
      jobCount: 28000,
      averageSalary: "7 200 zł",
      topProfessions: ["Programista", "UX Designer", "DevOps Engineer"],
      growthRate: 12.8,
      companies: 8200,
    },
    {
      id: 3,
      name: "Śląskie",
      demandLevel: "Wysoki",
      jobCount: 22000,
      averageSalary: "7 800 zł",
      topProfessions: ["Inżynier", "Programista", "Analityk biznesowy"],
      growthRate: 10.5,
      companies: 6800,
    },
    {
      id: 4,
      name: "Wielkopolskie",
      demandLevel: "Średni",
      jobCount: 18000,
      averageSalary: "6 900 zł",
      topProfessions: ["Programista", "Specjalista HR", "Manager sprzedaży"],
      growthRate: 9.2,
      companies: 5400,
    },
    {
      id: 5,
      name: "Dolnośląskie",
      demandLevel: "Średni",
      jobCount: 16000,
      averageSalary: "7 100 zł",
      topProfessions: ["Programista", "Tester", "Business Analyst"],
      growthRate: 8.7,
      companies: 4900,
    },
    {
      id: 6,
      name: "Pomorskie",
      demandLevel: "Średni",
      jobCount: 12000,
      averageSalary: "6 800 zł",
      topProfessions: ["Programista", "Grafik", "Specjalista logistyki"],
      growthRate: 7.3,
      companies: 3600,
    },
  ]

  const professions: Profession[] = [
    {
      id: 1,
      name: "Programista",
      category: "IT",
      demandByRegion: [
        { region: "Mazowieckie", demand: 15000, salary: "12 000 zł", companies: 2500 },
        { region: "Małopolskie", demand: 8500, salary: "10 500 zł", companies: 1800 },
        { region: "Śląskie", demand: 6200, salary: "11 200 zł", companies: 1400 },
        { region: "Wielkopolskie", demand: 4800, salary: "9 800 zł", companies: 1100 },
        { region: "Dolnośląskie", demand: 4200, salary: "10 200 zł", companies: 950 },
        { region: "Pomorskie", demand: 3100, salary: "9 500 zł", companies: 720 },
      ],
      totalDemand: 41800,
      averageSalary: "10 500 zł",
    },
    {
      id: 2,
      name: "Analityk danych",
      category: "IT",
      demandByRegion: [
        { region: "Mazowieckie", demand: 3200, salary: "9 500 zł", companies: 450 },
        { region: "Małopolskie", demand: 1800, salary: "8 200 zł", companies: 280 },
        { region: "Śląskie", demand: 1400, salary: "8 800 zł", companies: 220 },
        { region: "Wielkopolskie", demand: 950, salary: "7 800 zł", companies: 180 },
        { region: "Dolnośląskie", demand: 850, salary: "8 100 zł", companies: 160 },
        { region: "Pomorskie", demand: 600, salary: "7 500 zł", companies: 120 },
      ],
      totalDemand: 8800,
      averageSalary: "8 300 zł",
    },
    {
      id: 3,
      name: "UX Designer",
      category: "Design",
      demandByRegion: [
        { region: "Mazowieckie", demand: 2800, salary: "8 200 zł", companies: 380 },
        { region: "Małopolskie", demand: 2200, salary: "7 500 zł", companies: 320 },
        { region: "Śląskie", demand: 1100, salary: "7 200 zł", companies: 180 },
        { region: "Wielkopolskie", demand: 850, salary: "6 800 zł", companies: 140 },
        { region: "Dolnośląskie", demand: 750, salary: "7 000 zł", companies: 125 },
        { region: "Pomorskie", demand: 580, salary: "6 500 zł", companies: 95 },
      ],
      totalDemand: 8280,
      averageSalary: "7 200 zł",
    },
  ]

  const professionCategories = ["Wszystkie", "IT", "Design", "Marketing", "HR", "Inżynieria"]

  const getDemandColor = (level: string) => {
    switch (level) {
      case "Bardzo wysoki":
        return "bg-red-500"
      case "Wysoki":
        return "bg-orange-500"
      case "Średni":
        return "bg-yellow-500"
      case "Niski":
        return "bg-green-500"
      default:
        return "bg-gray-500"
    }
  }

  const getDemandBadgeColor = (level: string) => {
    switch (level) {
      case "Bardzo wysoki":
        return "text-red-600 bg-red-50 border-red-200"
      case "Wysoki":
        return "text-orange-600 bg-orange-50 border-orange-200"
      case "Średni":
        return "text-yellow-600 bg-yellow-50 border-yellow-200"
      case "Niski":
        return "text-green-600 bg-green-50 border-green-200"
      default:
        return "text-gray-600 bg-gray-50 border-gray-200"
    }
  }

  const filteredRegions = regions.filter((region) => region.name.toLowerCase().includes(searchTerm.toLowerCase()))

  const selectedRegionData = selectedRegion ? regions.find((r) => r.name === selectedRegion) : null

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Mapa zawodów</h1>
          <p className="text-gray-600">Sprawdź zapotrzebowanie na zawody w różnych regionach Polski</p>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Szukaj regionu..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-200"
                />
              </div>
            </div>

            {/* Profession Filter */}
            <div className="flex gap-2 flex-wrap">
              {professionCategories.map((category) => (
                <Button
                  key={category}
                  variant={selectedProfession === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedProfession(category)}
                  className={cn(
                    selectedProfession === category
                      ? "bg-[#2bcaca] hover:bg-[#229999] text-white"
                      : "text-gray-600 hover:text-gray-900",
                  )}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map/Regions List */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Regiony Polski</h2>
                <div className="flex items-center space-x-4 text-sm">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 mr-2"></div>
                    <span>Bardzo wysoki</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-500 mr-2"></div>
                    <span>Wysoki</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-yellow-500 mr-2"></div>
                    <span>Średni</span>
                  </div>
                </div>
              </div>

              {/* Simplified Map Visualization */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {filteredRegions.map((region) => (
                  <div
                    key={region.id}
                    className={cn(
                      "p-4 border-2 cursor-pointer transition-all hover:shadow-md",
                      selectedRegion === region.name
                        ? "border-[#2bcaca] bg-[#2bcaca]/5"
                        : "border-gray-200 hover:border-gray-300",
                    )}
                    onClick={() => setSelectedRegion(region.name)}
                  >
                    <div className="flex items-center mb-2">
                      <div className={cn("w-3 h-3 mr-2", getDemandColor(region.demandLevel))}></div>
                      <h3 className="font-medium text-gray-900">{region.name}</h3>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center justify-between">
                        <span>Oferty pracy:</span>
                        <span className="font-medium">{region.jobCount.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Śr. zarobki:</span>
                        <span className="font-medium">{region.averageSalary}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Wzrost:</span>
                        <span className="font-medium text-green-600">+{region.growthRate}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Top Professions by Demand */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Najpopularniejsze zawody</h3>
                <div className="space-y-3">
                  {professions.map((profession) => (
                    <div key={profession.id} className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex items-center">
                        <Target className="h-5 w-5 text-[#2bcaca] mr-3" />
                        <div>
                          <h4 className="font-medium text-gray-900">{profession.name}</h4>
                          <p className="text-sm text-gray-500">{profession.category}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900">{profession.totalDemand.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">ofert</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Region Details */}
          <div className="space-y-6">
            {selectedRegionData ? (
              <>
                <div className="bg-white p-6 shadow-sm border border-gray-200">
                  <div className="flex items-center mb-4">
                    <MapPin className="h-6 w-6 text-[#2bcaca] mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900">{selectedRegionData.name}</h2>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Zapotrzebowanie:</span>
                      <Badge variant="outline" className={getDemandBadgeColor(selectedRegionData.demandLevel)}>
                        {selectedRegionData.demandLevel}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Oferty pracy:</span>
                      <span className="font-medium">{selectedRegionData.jobCount.toLocaleString()}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Średnie zarobki:</span>
                      <span className="font-medium">{selectedRegionData.averageSalary}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Wzrost roczny:</span>
                      <span className="font-medium text-green-600">+{selectedRegionData.growthRate}%</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Liczba firm:</span>
                      <span className="font-medium">{selectedRegionData.companies.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 shadow-sm border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Top zawody w regionie</h3>
                  <div className="space-y-3">
                    {selectedRegionData.topProfessions.map((profession, index) => (
                      <div key={index} className="flex items-center p-3 bg-[#2bcaca]/5">
                        <div className="w-6 h-6 bg-[#2bcaca] flex items-center justify-center text-white text-sm font-medium mr-3">
                          {index + 1}
                        </div>
                        <span className="font-medium text-gray-900">{profession}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white p-6 shadow-sm border border-gray-200">
                <div className="text-center py-8">
                  <Info className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Wybierz region</h3>
                  <p className="text-gray-500">Kliknij na region, aby zobaczyć szczegółowe informacje o rynku pracy</p>
                </div>
              </div>
            )}

            {/* Statistics */}
            <div className="bg-white p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Statystyki ogólne</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <BarChart3 className="h-4 w-4 text-[#2bcaca] mr-2" />
                    <span className="text-sm text-gray-600">Łączne oferty pracy:</span>
                  </div>
                  <span className="font-medium">
                    {regions.reduce((sum, r) => sum + r.jobCount, 0).toLocaleString()}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Building className="h-4 w-4 text-[#2bcaca] mr-2" />
                    <span className="text-sm text-gray-600">Łączne firmy:</span>
                  </div>
                  <span className="font-medium">
                    {regions.reduce((sum, r) => sum + r.companies, 0).toLocaleString()}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <TrendingUp className="h-4 w-4 text-[#2bcaca] mr-2" />
                    <span className="text-sm text-gray-600">Średni wzrost:</span>
                  </div>
                  <span className="font-medium text-green-600">
                    +{(regions.reduce((sum, r) => sum + r.growthRate, 0) / regions.length).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
